// store/v3/bootstrap.ts — boot / sync / teardown orchestration (SPEC §3 BootstrapStoreApi, §7)
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  DataSource, DataSourceContext, EntityVersions, Unsubscribe, Versioned,
} from '~/types/v3/datasource'
import type { QrToken } from '~/types/v3/token'
import type {
  ChannelPriceMap, ProductAllowMap, ProductsPayload, SettingsPayload, StoreProfile,
} from '~/types/v3/entities'
import type { BootstrapStatus, BootstrapStep, SessionEndReason } from '~/types/v3/session'
import type { BootstrapStoreApi } from '~/types/v3/stores'
import { cacheKey, menuSetKey } from '~/types/v3/cache'
import { readV3Config } from '~/services/v3/config'
import { createDataSource, disposeDataSource } from '~/services/v3/datasource'
import { entityCache } from '~/services/v3/cache/entityCache'
import { pruneCarts } from '~/services/v3/cache/cartStorage'
import { addBreadcrumb, captureError, setSessionContext } from '~/services/v3/monitoring'
import { isTokenExpired } from '~/utils/v3/time'
import { useSessionStore } from '~/store/v3/session'
import { useCatalogStore } from '~/store/v3/catalog'
import { useTableStore } from '~/store/v3/table'
import { useCartStore } from '~/store/v3/cart'
import { useUiStore } from '~/store/v3/ui'

const REVALIDATE_THROTTLE_MS = 60_000
const PROFILE_TTL_MS = 24 * 60 * 60 * 1000

type SyncableEntity = 'settings' | 'products' | 'channel_price' | 'menuSet'

/**
 * Result of the token check. `ok: false` carries the teardown reason, so a
 * missing document ('paid') stays distinct from a lapsed one ('expired') — and
 * from a network failure, which throws rather than resolving.
 */
type VerifyOutcome =
  | { ok: true; token: QrToken }
  | { ok: false; reason: Extract<SessionEndReason, 'paid' | 'expired'> }

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  return typeof e === 'string' ? e : 'unknown_error'
}

const SOLDOUT_REMOVED_MESSAGE = 'มีรายการสินค้าหมด ถูกนำออกจากตะกร้าแล้ว'

export const useBootstrapStore = defineStore('v3-bootstrap', () => {
  const status = ref<BootstrapStatus>('idle')
  const step = ref<BootstrapStep>('restore')
  const error = ref<string | null>(null)
  const lastRevalidatedAt = ref(0)
  const activeQrcodeId = ref<string | null>(null)

  /** Every listener created for the current session (SPEC §0). */
  let subs: Unsubscribe[] = []
  let ds: DataSource | null = null
  let inflight: Promise<void> | null = null
  /**
   * Bumped by every teardown. `doRun()` captures it and bails out if the session
   * it was booting was torn down while it awaited (a `watchQrcode(null)` landing
   * mid-run must not be overwritten by `status = 'ready'`).
   */
  let generation = 0

  /** Drops every listener this store created. Safe to call repeatedly. */
  function unsubscribeAll(): void {
    for (const unsub of subs) {
      try { unsub() } catch (e) { console.warn('[v3/bootstrap] unsubscribe failed', e) }
    }
    subs = []
  }

  /* ---------------------------------------------------------------- */
  /* hydrate                                                           */
  /* ---------------------------------------------------------------- */

  async function hydrateFromCache(storeId: string, settingId: string | null): Promise<void> {
    const catalog = useCatalogStore()

    const [settings, products, channelPrice, profile, menuSet] = await Promise.all([
      entityCache.get<SettingsPayload>(cacheKey(storeId, 'settings')),
      entityCache.get<ProductsPayload>(cacheKey(storeId, 'products')),
      entityCache.get<ChannelPriceMap>(cacheKey(storeId, 'channel_price')),
      entityCache.get<StoreProfile>(cacheKey(storeId, 'profile')),
      settingId
        ? entityCache.get<ProductAllowMap>(menuSetKey(storeId, settingId))
        : Promise.resolve(undefined),
    ])

    if (settings) catalog.setSettings({ version: settings.version, data: settings.data })
    if (products) catalog.setProducts({ version: products.version, data: products.data })
    if (channelPrice) catalog.setChannelPrice({ version: channelPrice.version, data: channelPrice.data })
    if (menuSet) catalog.setMenuSet({ version: menuSet.version, data: menuSet.data })
    if (profile?.data?.name) catalog.storeProfileName = profile.data.name

    // "all four present" — with no settingId there is no menu set to wait for.
    catalog.hydrated = !!settings && !!products && !!channelPrice && (settingId ? !!menuSet : true)
  }

  /* ---------------------------------------------------------------- */
  /* sync                                                              */
  /* ---------------------------------------------------------------- */

  async function syncEntity<T>(opts: {
    entity: SyncableEntity
    key: string
    remoteVersion: string | undefined
    ttlMs: number
    fetch: () => Promise<Versioned<T>>
    apply: (v: Versioned<T>) => void
  }): Promise<void> {
    const catalog = useCatalogStore()
    const cached = await entityCache.get<T>(opts.key)

    // Backend exposes versions: fetch only when the version moved.
    if (opts.remoteVersion !== undefined) {
      if (opts.remoteVersion === catalog.versions[opts.entity]) return
      const fresh = await opts.fetch()
      await entityCache.set<T>(opts.key, { version: fresh.version, fetchedAt: Date.now(), data: fresh.data })
      opts.apply(fresh)
      return
    }

    // No versions: TTL guards the refetch, the content hash guards the write.
    if (cached && Date.now() - cached.fetchedAt < opts.ttlMs) {
      if (catalog.versions[opts.entity] !== cached.version) {
        opts.apply({ version: cached.version, data: cached.data })
      }
      return
    }

    const fresh = await opts.fetch()
    if (!cached || cached.version !== fresh.version) {
      await entityCache.set<T>(opts.key, { version: fresh.version, fetchedAt: Date.now(), data: fresh.data })
      opts.apply(fresh)
    } else {
      // unchanged — only touch fetchedAt so the TTL restarts
      await entityCache.set<T>(opts.key, { version: cached.version, fetchedAt: Date.now(), data: cached.data })
      if (catalog.versions[opts.entity] !== cached.version) {
        opts.apply({ version: cached.version, data: cached.data })
      }
    }
  }

  async function syncProfile(storeId: string): Promise<void> {
    const catalog = useCatalogStore()
    if (!ds) return
    const hasName = catalog.settings.some((r) => r.settingId === 'name' && !!r.value)
    if (hasName) return

    const key = cacheKey(storeId, 'profile')
    const cached = await entityCache.get<StoreProfile>(key)
    if (cached && Date.now() - cached.fetchedAt < PROFILE_TTL_MS) {
      if (cached.data?.name) catalog.storeProfileName = cached.data.name
      return
    }
    const profile = await ds.getStoreProfile()
    if (profile?.name) {
      await entityCache.set<StoreProfile>(key, { version: profile.name, fetchedAt: Date.now(), data: profile })
      catalog.storeProfileName = profile.name
    }
  }

  /**
   * Brings settings / products / channel_price / menuSet up to date.
   * `versions` is null (or the source has no version node) => TTL mode.
   */
  async function syncEntities(versions: EntityVersions | null): Promise<void> {
    const session = useSessionStore()
    const catalog = useCatalogStore()
    const storeId = session.storeId
    if (!ds || !storeId) return

    const settingId = session.settingId
    const ttlMs = readV3Config().rtdbTtlMs
    const useVersions = ds.supportsVersions && !!versions
    const source = ds

    await syncEntity<SettingsPayload>({
      entity: 'settings',
      key: cacheKey(storeId, 'settings'),
      remoteVersion: useVersions ? versions!.settings : undefined,
      ttlMs,
      fetch: () => source.fetchSettings(),
      apply: (v) => catalog.setSettings(v),
    })

    await syncEntity<ProductsPayload>({
      entity: 'products',
      key: cacheKey(storeId, 'products'),
      remoteVersion: useVersions ? versions!.products : undefined,
      ttlMs,
      fetch: () => source.fetchProducts(),
      apply: (v) => catalog.setProducts(v),
    })

    await syncEntity<ChannelPriceMap>({
      entity: 'channel_price',
      key: cacheKey(storeId, 'channel_price'),
      remoteVersion: useVersions ? versions!.channel_price : undefined,
      ttlMs,
      fetch: () => source.fetchChannelPrice(),
      apply: (v) => catalog.setChannelPrice(v),
    })

    if (settingId) {
      await syncEntity<ProductAllowMap>({
        entity: 'menuSet',
        key: menuSetKey(storeId, settingId),
        remoteVersion: useVersions ? versions!.menuSet : undefined,
        ttlMs,
        fetch: () => source.fetchMenuSet(settingId),
        apply: (v) => catalog.setMenuSet(v),
      })
    }

    await syncProfile(storeId)
  }

  /* ---------------------------------------------------------------- */
  /* boot steps — one job each, sequenced by doRun()                    */
  /* ---------------------------------------------------------------- */

  /**
   * True when the session this run was booting has been torn down (paid /
   * expired / switched to another QR code) while the run was awaiting. Such a
   * run must touch nothing: the teardown, or the run that replaced it, owns
   * the store now.
   */
  function isStale(runGeneration: number): boolean {
    return runGeneration !== generation
  }

  /** Restores the persisted session and returns the identity every later step needs. */
  function stepRestoreSession(): DataSourceContext | null {
    step.value = 'restore'
    const session = useSessionStore()

    if (!session.restore() || !session.token) {
      status.value = 'error'
      error.value = 'no_session'
      return null
    }

    return {
      storeId: session.storeId as string,
      table: session.table as string,
      qrcodeId: session.qrcodeId as string,
      settingId: session.settingId,
    }
  }

  /** Builds and initialises the configured data source, and tags the session for Sentry. */
  async function stepCreateSource(ctx: DataSourceContext): Promise<DataSource> {
    step.value = 'source'
    const session = useSessionStore()
    const cfg = readV3Config()

    const source = createDataSource(cfg)
    ds = source
    await source.init(ctx)

    setSessionContext(session.token, { dataSource: cfg.dataSource, writeMode: cfg.writeMode })
    addBreadcrumb('[v3] bootstrap source ready', {
      kind: source.kind, storeId: ctx.storeId, qrcodeId: ctx.qrcodeId,
    })
    return source
  }

  /**
   * Asks the backend whether this QR code is still orderable, BEFORE any data
   * is loaded: a paid or expired table must reach `/v3/login` without the
   * boot doing further work. A network failure throws instead of resolving, so
   * a flaky connection surfaces as a retryable error, never as "expired".
   */
  async function stepVerifyToken(ctx: DataSourceContext, source: DataSource): Promise<VerifyOutcome> {
    step.value = 'verify_token'
    const verified = await source.getQrcode(ctx.qrcodeId)

    if (!verified) return { ok: false, reason: 'paid' }
    if (isTokenExpired(verified)) return { ok: false, reason: 'expired' }
    return { ok: true, token: verified }
  }

  /** Fills the catalog from IndexedDB so the UI can paint before any network sync. */
  async function stepHydrateFromCache(ctx: DataSourceContext): Promise<void> {
    step.value = 'hydrate'
    await hydrateFromCache(ctx.storeId, ctx.settingId)
  }

  /**
   * Loads this QR code's persisted cart. Needed on a reload (the entry page is
   * not visited again) and after a QR switch (teardown drops the in-memory
   * copy). Re-entrant: a cart already on the wanted key is left alone.
   */
  async function stepRestoreCart(): Promise<void> {
    const session = useSessionStore()
    const cart = useCartStore()

    const wantedCartKey = session.cartKey
    if (wantedCartKey && cart.key !== wantedCartKey) {
      await cart.restore(wantedCartKey)
    }
  }

  /** Subscribes to the small realtime documents and records their unsubscribes. */
  function stepAttachWatchers(ctx: DataSourceContext, source: DataSource): void {
    step.value = 'watch'
    const session = useSessionStore()
    const tableStore = useTableStore()
    const catalog = useCatalogStore()

    subs.push(source.watchQrcode(ctx.qrcodeId, (t) => {
      if (!t) teardown('paid')
      else session.token = t
    }))
    subs.push(source.watchTableState(ctx.table, (s) => tableStore.setState(s)))
    subs.push(source.watchTableItems(ctx.table, (d) => tableStore.setItems(d)))
    subs.push(source.watchSoldout((m) => {
      catalog.setSoldout(m)
      // a product can sell out while it is already in the cart — drop it there too
      const dropped = useCartStore().pruneSoldout()
      if (dropped > 0) useUiStore().showToast({ text: SOLDOUT_REMOVED_MESSAGE, icon: 'warning' })
    }))
    subs.push(source.watchVersions((v) => {
      void syncEntities(v).catch((e) => {
        console.warn('[v3/bootstrap] version sync failed', e)
      })
    }))
  }

  /** Brings the catalog up to date. Tolerated when the cache already served one. */
  async function stepInitialSync(ctx: DataSourceContext, source: DataSource): Promise<void> {
    step.value = 'sync'
    const catalog = useCatalogStore()

    try {
      await syncEntities(await source.getVersions())
    } catch (e) {
      if (!catalog.hydrated) throw e
      console.warn('[v3/bootstrap] sync failed, serving cached catalog', e)
      captureError(e, { phase: 'sync', storeId: ctx.storeId, qrcodeId: ctx.qrcodeId })
    }
  }

  /** Marks the session ready and drops carts left behind by older QR codes. */
  function stepFinish(ctx: DataSourceContext): void {
    step.value = 'done'
    lastRevalidatedAt.value = Date.now()
    status.value = 'ready'
    activeQrcodeId.value = ctx.qrcodeId
    void pruneCarts().catch((e) => console.warn('[v3/bootstrap] pruneCarts failed', e))
  }

  /* ---------------------------------------------------------------- */
  /* run                                                               */
  /* ---------------------------------------------------------------- */

  async function doRun(): Promise<void> {
    // A retry (status 'error') re-enters here: never stack a second set of
    // listeners on top of the ones a partial run already created.
    unsubscribeAll()
    const runGeneration = generation

    status.value = 'loading'
    error.value = null

    try {
      const ctx = stepRestoreSession()
      if (!ctx) return

      const source = await stepCreateSource(ctx)
      if (isStale(runGeneration)) return

      const verified = await stepVerifyToken(ctx, source)
      // Check staleness BEFORE adopting the token: a switch that landed while
      // the verification was in flight must not get the old token written back.
      if (isStale(runGeneration)) return
      if (!verified.ok) {
        teardown(verified.reason)
        return
      }
      useSessionStore().token = verified.token

      await stepHydrateFromCache(ctx)
      if (isStale(runGeneration)) return

      await stepRestoreCart()
      // Last check before attaching listeners: a stale run must never push
      // subscriptions the teardown that superseded it can no longer see.
      if (isStale(runGeneration)) return

      stepAttachWatchers(ctx, source)

      await stepInitialSync(ctx, source)
      if (isStale(runGeneration)) return

      stepFinish(ctx)
    } catch (e) {
      if (isStale(runGeneration)) return
      status.value = 'error'
      error.value = errorMessage(e)
      captureError(e, { phase: 'bootstrap', step: step.value })
    }
  }

  /**
   * Boots the session. Idempotent: concurrent callers share the in-flight
   * promise, a ready bootstrap for the same QR code is a no-op, and a different
   * QR code tears the old session down ('switch') before booting again.
   */
  async function run(): Promise<void> {
    if (inflight) return inflight

    const session = useSessionStore()
    if (status.value === 'ready') {
      session.restore()
      const id = session.qrcodeId
      if (id && activeQrcodeId.value === id) return
      // teardown('switch') clears the session; re-arm the token that came with
      // the new QR code so the fresh run boots it.
      const carried = session.token
      teardown('switch')
      if (carried && carried.type === 'guest') session.start(carried)
    }

    // `teardown()` drops `inflight`, so a superseded run must not clear the
    // in-flight promise of the run that replaced it.
    const started = doRun()
    inflight = started
    void started.finally(() => { if (inflight === started) inflight = null })
    return started
  }

  /* ---------------------------------------------------------------- */
  /* revalidate                                                        */
  /* ---------------------------------------------------------------- */

  async function revalidate(): Promise<void> {
    if (status.value !== 'ready') return
    const session = useSessionStore()
    if (session.isExpired()) {
      teardown('expired')
      return
    }
    const now = Date.now()
    if (now - lastRevalidatedAt.value <= REVALIDATE_THROTTLE_MS) return
    lastRevalidatedAt.value = now
    try {
      await syncEntities(ds ? await ds.getVersions() : null)
    } catch (e) {
      console.warn('[v3/bootstrap] revalidate failed', e)
    }
  }

  /* ---------------------------------------------------------------- */
  /* teardown                                                          */
  /* ---------------------------------------------------------------- */

  function teardown(reason: SessionEndReason): void {
    const session = useSessionStore()
    const catalog = useCatalogStore()
    const tableStore = useTableStore()
    const cart = useCartStore()

    generation += 1
    // Any run still awaiting belongs to the session we are dropping: forget it
    // so the next `run()` boots the new session instead of adopting its promise.
    inflight = null
    unsubscribeAll()

    // disposeDataSource() disposes the memoised instance exactly once.
    ds = null
    disposeDataSource()

    catalog.reset()
    tableStore.reset()

    if (reason === 'paid' || reason === 'expired' || reason === 'logout') {
      cart.clear()
    } else if (reason === 'switch') {
      // Keep the persisted cart of the previous QR code; drop it from memory
      // only. `key` goes first so nothing can persist over the old cart.
      cart.key = null
      cart.items = []
      cart.pendingJobId = null
    }

    session.end(reason)

    status.value = reason === 'switch' ? 'idle' : 'expired'
    step.value = 'restore'
    error.value = null
    activeQrcodeId.value = null
    setSessionContext(null)
  }

  return {
    status,
    step,
    error,
    lastRevalidatedAt,
    activeQrcodeId,
    run,
    revalidate,
    teardown,
  } satisfies BootstrapStoreApi
})

export type BootstrapStore = ReturnType<typeof useBootstrapStore>
